'use client';

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowLeft, Brain, TrendingUp, Target, Zap, CheckCircle, Code, Database, BarChart3, Cpu, Eye, MessageSquare, Sparkles } from "lucide-react";
import { useParams } from "next/navigation";
import { localizeSpelling } from "@/lib/spelling-localization";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const machineLearningData = {
  title: "Machine Learning",
  subtitle: "Advanced AI algorithms that learn from data to make intelligent predictions and decisions",
  headerGradient: "bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-500",
  accentColor: "text-purple-600",
  buttonColor: "bg-purple-600 hover:bg-purple-700",
  
  overview: "Machine Learning is at the core of modern AI, enabling systems to automatically learn and improve from experience without being explicitly programmed. Our ML solutions transform raw data into actionable insights, automate complex decision-making processes, and unlock new possibilities for innovation across industries.",
  
  capabilities: [
    {
      name: "Supervised Learning",
      description: "Train models on labeled data to make predictions on new, unseen data",
      icon: Target,
      applications: ["Classification", "Regression", "Predictive Analytics", "Risk Assessment"],
      useCases: ["Customer churn prediction", "Medical diagnosis", "Fraud detection", "Price optimization"]
    },
    {
      name: "Unsupervised Learning", 
      description: "Discover hidden patterns and structures in unlabeled data",
      icon: Eye,
      applications: ["Clustering", "Dimensionality Reduction", "Anomaly Detection", "Association Rules"],
      useCases: ["Customer segmentation", "Market basket analysis", "Network intrusion detection", "Gene sequencing"]
    },
    {
      name: "Reinforcement Learning",
      description: "Learn optimal actions through trial and error in dynamic environments",
      icon: Zap,
      applications: ["Game Playing", "Robotics", "Autonomous Systems", "Resource Optimization"],
      useCases: ["Autonomous vehicles", "Trading algorithms", "Recommendation systems", "Supply chain optimization"]
    }
  ],
  
  technologies: [
    {
      category: "Deep Learning Frameworks",
      tools: ["TensorFlow", "PyTorch", "Keras", "JAX", "MXNet"],
      description: "Advanced neural network architectures for complex pattern recognition"
    },
    {
      category: "Classical ML Libraries",
      tools: ["Scikit-learn", "XGBoost", "LightGBM", "CatBoost", "Random Forest"],
      description: "Proven algorithms for structured data and traditional ML tasks"
    },
    {
      category: "Data Processing",
      tools: ["Pandas", "NumPy", "Apache Spark", "Dask", "Polars"],
      description: "Efficient data manipulation and preprocessing at scale"
    },
    {
      category: "Model Deployment",
      tools: ["MLflow", "Kubeflow", "TensorFlow Serving", "ONNX", "Docker"],
      description: "Production-ready model serving and lifecycle management"
    },
    {
      category: "Cloud Platforms",
      tools: ["AWS SageMaker", "Google AI Platform", "Azure ML", "Databricks", "Vertex AI"],
      description: "Scalable cloud infrastructure for ML workloads"
    },
    {
      category: "Specialized AI",
      tools: ["OpenCV", "spaCy", "Hugging Face", "LangChain", "Stable Diffusion"],
      description: "Domain-specific tools for computer vision, NLP, and generative AI"
    }
  ],
  
  industries: [
    {
      name: "Healthcare",
      applications: ["Medical imaging analysis", "Drug discovery", "Personalized treatment", "Epidemic modeling"],
      impact: "95% accuracy in diagnostic imaging, 40% faster drug discovery"
    },
    {
      name: "Finance",
      applications: ["Algorithmic trading", "Credit scoring", "Fraud detection", "Risk management"],
      impact: "60% reduction in false positives, 25% improvement in trading returns"
    },
    {
      name: "Retail & E-commerce",
      applications: ["Recommendation engines", "Demand forecasting", "Price optimization", "Inventory management"],
      impact: "30% increase in conversion rates, 20% reduction in inventory costs"
    },
    {
      name: "Manufacturing",
      applications: ["Predictive maintenance", "Quality control", "Supply chain optimization", "Process automation"],
      impact: "50% reduction in downtime, 35% improvement in quality metrics"
    },
    {
      name: "Transportation",
      applications: ["Route optimization", "Autonomous vehicles", "Traffic management", "Predictive maintenance"],
      impact: "25% reduction in fuel costs, 40% improvement in delivery times"
    },
    {
      name: "Energy",
      applications: ["Smart grid optimization", "Renewable energy forecasting", "Equipment monitoring", "Demand prediction"],
      impact: "20% improvement in energy efficiency, 30% better renewable integration"
    }
  ],
  
  process: [
    {
      step: "1",
      title: "Data Collection & Preparation",
      description: "Gather, clean, and preprocess data from various sources to ensure quality and consistency",
      details: ["Data auditing and quality assessment", "Feature engineering and selection", "Data augmentation and synthetic data generation", "Handling missing values and outliers"]
    },
    {
      step: "2", 
      title: "Model Development",
      description: "Design and train machine learning models tailored to your specific business objectives",
      details: ["Algorithm selection and hyperparameter tuning", "Cross-validation and performance evaluation", "Ensemble methods and model stacking", "Transfer learning and fine-tuning"]
    },
    {
      step: "3",
      title: "Validation & Testing",
      description: "Rigorously test models to ensure reliability, accuracy, and generalization",
      details: ["A/B testing and statistical validation", "Bias detection and fairness assessment", "Robustness testing and adversarial validation", "Performance monitoring and drift detection"]
    },
    {
      step: "4",
      title: "Deployment & Monitoring",
      description: "Deploy models to production with continuous monitoring and optimization",
      details: ["Scalable model serving infrastructure", "Real-time monitoring and alerting", "Automated retraining pipelines", "Performance tracking and optimization"]
    }
  ],
  
  benefits: [
    "Automate complex decision-making processes",
    "Uncover hidden patterns in large datasets", 
    "Improve accuracy and reduce human error",
    "Scale intelligent operations efficiently",
    "Adapt and learn from new data continuously",
    "Optimize resource allocation and costs",
    "Enable predictive and proactive strategies",
    "Personalize experiences at scale"
  ]
};

export default function MachineLearningPage() {
  const params = useParams();
  const currentLocale = params?.locale as string || 'en';
  
  // Function to localize text
  const localize = (text: string) => localizeSpelling(text, currentLocale);
  
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Section */}
      <header className={`py-20 md:py-28 text-white ${machineLearningData.headerGradient} relative overflow-hidden`}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, white 2px, transparent 2px),
                             radial-gradient(circle at 75% 75%, white 2px, transparent 2px)`,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="container mx-auto max-w-6xl px-4 relative z-10">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Link href={`/${currentLocale}`} className="inline-flex items-center text-sm text-white/80 hover:text-white transition-colors duration-300 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <ArrowLeft size={18} className="mr-2" />
              {localize("Back to Homepage")}
            </Link>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
                {localize(machineLearningData.title)}
              </h1>
              <p className="text-xl text-purple-100 mb-8 leading-relaxed">
                {localize(machineLearningData.subtitle)}
              </p>
              <div className="flex flex-wrap gap-3">
                {["AI", "Deep Learning", "Neural Networks", "Predictive Analytics"].map((tag, index) => (
                  <Badge key={tag} variant="secondary" className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                    {localize(tag)}
                  </Badge>
                ))}
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <Brain className="w-12 h-12 mx-auto mb-3 text-cyan-200" />
                    <div className="text-2xl font-bold">95%</div>
                    <div className="text-sm text-purple-100">{localize("Accuracy Rate")}</div>
                  </div>
                  <div className="text-center">
                    <TrendingUp className="w-12 h-12 mx-auto mb-3 text-cyan-200" />
                    <div className="text-2xl font-bold">10x</div>
                    <div className="text-sm text-purple-100">{localize("Faster Processing")}</div>
                  </div>
                  <div className="text-center">
                    <Database className="w-12 h-12 mx-auto mb-3 text-cyan-200" />
                    <div className="text-2xl font-bold">1M+</div>
                    <div className="text-sm text-purple-100">{localize("Data Points")}</div>
                  </div>
                  <div className="text-center">
                    <BarChart3 className="w-12 h-12 mx-auto mb-3 text-cyan-200" />
                    <div className="text-2xl font-bold">24/7</div>
                    <div className="text-sm text-purple-100">{localize("Monitoring")}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto max-w-6xl px-4 py-16">
        
        {/* Overview Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-slate-200">
            <h2 className={`text-3xl font-bold mb-6 ${machineLearningData.accentColor}`}>
              {localize("What is Machine Learning?")}
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed">
              {localize(machineLearningData.overview)}
            </p>
          </div>
        </motion.section>

        {/* Core Capabilities */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Core ML Capabilities")}
          </motion.h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {machineLearningData.capabilities.map((capability, index) => (
              <motion.div key={capability.name} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-l-4 border-l-purple-500">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <capability.icon className="w-6 h-6 text-purple-600" />
                      </div>
                      <CardTitle className="text-xl">{localize(capability.name)}</CardTitle>
                    </div>
                    <CardDescription className="text-base">
                      {localize(capability.description)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Applications")}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {capability.applications.map((app) => (
                            <Badge key={app} variant="outline" className="text-xs">
                              {localize(app)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Use Cases")}
                        </h4>
                        <ul className="space-y-1">
                          {capability.useCases.map((useCase) => (
                            <li key={useCase} className="text-sm text-slate-600 flex items-start">
                              <CheckCircle className="w-3 h-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                              {localize(useCase)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Technology Stack */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-12">
            {localize("Technology Stack")}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {machineLearningData.technologies.map((tech, index) => (
              <Card key={tech.category} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Code className="w-5 h-5 text-purple-600" />
                    {localize(tech.category)}
                  </CardTitle>
                  <CardDescription>{localize(tech.description)}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {tech.tools.map((tool) => (
                      <Badge key={tool} variant="secondary" className="text-xs">
                        {tool}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.section>

        {/* Industry Applications */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Industry Applications")}
          </motion.h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {machineLearningData.industries.map((industry, index) => (
              <motion.div key={industry.name} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg">{localize(industry.name)}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Applications")}
                        </h4>
                        <ul className="space-y-1">
                          {industry.applications.map((app) => (
                            <li key={app} className="text-sm text-slate-600 flex items-start">
                              <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mr-2 mt-2 flex-shrink-0"></div>
                              {localize(app)}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="pt-3 border-t border-slate-200">
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Impact")}
                        </h4>
                        <p className="text-sm text-green-700 font-medium">{localize(industry.impact)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Our Process */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-12">
            {localize("Our ML Development Process")}
          </h2>
          
          <div className="space-y-8">
            {machineLearningData.process.map((step, index) => (
              <div key={step.step} className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {step.step}
                  </div>
                </div>
                <div className="flex-1">
                  <Card className="hover:shadow-lg transition-shadow duration-300">
                    <CardHeader>
                      <CardTitle className="text-xl">{localize(step.title)}</CardTitle>
                      <CardDescription className="text-base">{localize(step.description)}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="grid md:grid-cols-2 gap-2">
                        {step.details.map((detail) => (
                          <li key={detail} className="text-sm text-slate-600 flex items-start">
                            <CheckCircle className="w-3 h-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            {localize(detail)}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Benefits */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8 md:p-12 border border-purple-200">
            <h2 className="text-3xl font-bold text-center mb-8">
              {localize("Why Choose Machine Learning?")}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {machineLearningData.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-slate-700">{localize(benefit)}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="text-center"
        >
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-slate-200">
            <h2 className="text-3xl font-bold mb-4">
              {localize("Ready to Transform Your Business with Machine Learning?")}
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              {localize("Let our ML experts help you unlock the power of your data and build intelligent solutions that drive real business value.")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                href={`/${currentLocale}/contact`}
                className={`${machineLearningData.buttonColor} text-white font-semibold py-3 px-8 rounded-lg text-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105`}
              >
                {localize("Start Your ML Project")}
              </Link>
              <Link 
                href={`/${currentLocale}/services`}
                className="bg-slate-100 text-slate-700 font-semibold py-3 px-8 rounded-lg text-lg hover:bg-slate-200 transition-colors duration-300"
              >
                {localize("Explore Our Services")}
              </Link>
            </div>
          </div>
        </motion.section>
      </main>
         </div>
   );
} 